/**
 * Making a customer-write `VALIDATION_FAILED` body usable on screen.
 *
 * Two things stand in the way, both caused by the backend's own error shaping:
 *
 * 1. **The row index is gone.** `setup-app.ts`'s `exceptionFactory` flattens on `error.property`
 *    at every depth, so a nested address rejection arrives as a bare `title` — five addresses with
 *    two bad names produce two details both reading `{ field: 'title' }` (verified live
 *    2026-09-11). The *count* survives even though the position does not, and that is what
 *    `attributeAddressTitles` exploits.
 * 2. **Identical messages repeat.** Those two details carry the same string, so joining them
 *    naively produced the toast `Enter a valid title Enter a valid title` (ADO 118942, QA
 *    2026-09-11).
 *
 * The proper fix is backend-side — emit a dotted path (`addresses.3.title`) instead of a bare
 * property — at which point `attributeAddressTitles` can be deleted outright. Raised as such; this
 * module is the client-side stand-in until then.
 *
 * (2) is not specific to customers — the discount wizard hits it with two tier rows breaching one
 * limit — so it lives in `@/lib/validation/server-details`, re-exported here for this feature's
 * own consumers.
 */

import type { ValidationDetail } from '@/lib/validation/server-details';

export { dedupeMessages, type ValidationDetail } from '@/lib/validation/server-details';

/** The server's own Address Name rule, used **only** to attribute an error it has already
 *  returned — never to gate submission. `CreateCustomerAddressDto.title` carries
 *  `@Length(2, 50)` and `@Matches(/^[a-zA-Z\s]+$/)`, and `stopAtFirstError` means either failure
 *  arrives as the same `Enter a valid title`. Mirroring it as a *client* rule would entrench a
 *  constraint that appears nowhere in AC 102101 and is still with the BA; reading it to decide
 *  which row to outline adds no constraint and blocks nothing. */
function serverWouldRejectTitle(title: string): boolean {
  const trimmed = title.trim();
  return trimmed.length < 2 || trimmed.length > 50 || !/^[a-zA-Z\s]+$/.test(trimmed);
}

/**
 * Which address rows a set of bare `title` details refers to.
 *
 * Returns the row indices to mark, or `[]` when the answer cannot be known. The count guard is
 * what makes this safe: we mark rows only when exactly as many titles look server-invalid as the
 * server sent details. If the backend's rule ever changes, the counts stop agreeing and this
 * degrades to marking nothing — the toast still carries the reason — rather than outlining rows
 * that are actually fine. Marking the whole column was the first attempt and it was worse than
 * marking nothing: it told the admin all five names were wrong when two were (QA 2026-09-11).
 */
export function attributeAddressTitles(details: ValidationDetail[], titles: string[]): number[] {
  const count = details.filter((detail) => detail.field === 'title').length;
  if (count === 0) return [];
  const suspects = titles.reduce<number[]>(
    (acc, title, index) => (serverWouldRejectTitle(title) ? [...acc, index] : acc),
    [],
  );
  return suspects.length === count ? suspects : [];
}
